// Company stores (S4 shop, 2026-10-12) — the portal's client for ordering.
// API: apps/api/src/routes/portal-company-store-orders.ts. Every price here
// comes from the server (priceCart); the browser only sends products,
// colours, sizes, the address and how to pay. The store is always the
// signed-in person's own (the server reads it from their membership).
import { ApiError, api } from "@/lib/api";
import { TERMS_DAYS, type MyStore, type PaymentOption } from "@/lib/portal-store";

// ─── Shapes (what the API answers) ───────────────────────────────────────

export interface ColourQuantities {
  color: string;
  quantities: Record<string, number>;
}

export interface CartItem {
  productId: string;
  colorQuantities: ColourQuantities[];
}

export interface ShipTo {
  name: string;
  /** Not sent to the API (it has no company field); kept for the form. */
  company?: string;
  street1: string;
  street2?: string | null;
  city: string;
  stateCode: string;
  postalCode: string;
  residential: boolean;
  phone?: string | null;
}

export interface PricedItem {
  productId: string;
  name: string;
  quantity: number;
  colours: string[];
  each: number;
  total: number;
  lines: Array<{ label: string; amount: string }>;
  setup?: number;
  notice?: string;
}

export type PricedCart =
  | {
      status: "priced";
      items: PricedItem[];
      subtotal: number;
      shipping: number;
      total: number;
      transitDays: number | null;
      shippingNeedsAddress: boolean;
    }
  | { status: "blocked"; message: string; itemIndex: number | null };

export type OrderStatus =
  | "pending_approval"
  | "rejected"
  | "awaiting_payment"
  | "approved"
  | "paid"
  | "placed"
  | "cancelled"
  | (string & {});

export interface StoreOrder {
  id: string;
  reference: string;
  status: OrderStatus;
  paymentMethod: PaymentOption | null;
  mine: boolean;
  member?: { name: string | null; email: string | null };
  items: PricedItem[];
  shipTo: ShipTo | null;
  subtotal: number | null;
  shipping: number | null;
  tax: number | null;
  total: number | null;
  canPay: boolean;
  rejectedReason: string | null;
  createdAt: string;
  approvedAt: string | null;
  paidAt: string | null;
  placedAt: string | null;
}

export interface PaymentSession {
  url: string;
  sessionId: string;
}

export interface SubmitResult {
  order: StoreOrder;
  created: boolean;
  payment: PaymentSession | null;
  placed: boolean;
}

export interface ApproveResult {
  order: StoreOrder;
  priceChanged: { from: number; to: number } | null;
  payment: PaymentSession | null;
  placed: boolean;
}

// ─── Calls ───────────────────────────────────────────────────────────────

/** Only the rows and sizes with a quantity — what the API is sent. */
export function cleanItems(items: CartItem[]): CartItem[] {
  return items
    .map((i) => ({
      productId: i.productId,
      colorQuantities: i.colorQuantities
        .map((r) => ({
          color: r.color,
          quantities: Object.fromEntries(
            Object.entries(r.quantities).filter(([, n]) => Number.isInteger(n) && n > 0),
          ),
        }))
        .filter((r) => Object.keys(r.quantities).length > 0),
    }))
    .filter((i) => i.colorQuantities.length > 0);
}

/** The ship-to as the API takes it (blanks dropped). The API's address has
 *  no company line, so the company rides on the recipient line
 *  ("Sam Lee, Acme Co") — it reaches the label and the sales order. */
export function apiShipTo(s: ShipTo) {
  const company = s.company?.trim();
  const out: Record<string, unknown> = {
    name: (company ? `${s.name.trim()}, ${company}` : s.name.trim()).slice(0, 200),
    street1: s.street1.trim(),
    city: s.city.trim(),
    stateCode: s.stateCode.trim().toUpperCase(),
    postalCode: s.postalCode.trim(),
    residential: s.residential,
  };
  if (s.street2?.trim()) out.street2 = s.street2.trim();
  if (s.phone?.trim()) out.phone = s.phone.trim();
  return out;
}

export async function priceStoreCart(items: CartItem[], shipTo: ShipTo | null): Promise<PricedCart> {
  return api<PricedCart>("/portal/store/cart/price", {
    method: "POST",
    body: JSON.stringify(shipTo ? { items, shipTo: apiShipTo(shipTo) } : { items }),
  });
}

/** One product's "From $X each" on the grid: the server's own price for
 *  the smallest order (`quantity` pieces), one colour and size, the
 *  cheapest it found. Products it couldn't price aren't in the list. */
export interface StoreFromPrice {
  productId: string;
  each: number;
  quantity: number;
  setup?: number;
}

export const STORE_FROM_PRICES_KEY = ["portal-store-from-prices"] as const;

export async function fetchFromPrices(): Promise<StoreFromPrice[]> {
  return (await api<{ prices: StoreFromPrice[] }>("/portal/store/prices")).prices;
}

/** "From $18.40 each" + "for 12 pieces, plus $40.00 setup" — or null (no $0). */
export function fromPriceWords(p: StoreFromPrice | null | undefined): { lead: string; detail: string } | null {
  if (!p || !Number.isFinite(p.each) || p.each <= 0 || !Number.isInteger(p.quantity) || p.quantity <= 0) return null;
  const setup = p.setup && p.setup > 0 ? `, plus ${money(p.setup)} setup` : "";
  return { lead: `From ${money(p.each)} each`, detail: `for ${pieces(p.quantity)}${setup}` };
}

export async function submitStoreOrder(a: {
  items: CartItem[];
  shipTo: ShipTo;
  paymentMethod: PaymentOption;
  idempotencyKey: string;
}): Promise<SubmitResult> {
  return api<SubmitResult>("/portal/store/orders", {
    method: "POST",
    body: JSON.stringify({ items: a.items, shipTo: apiShipTo(a.shipTo), paymentMethod: a.paymentMethod, idempotencyKey: a.idempotencyKey }),
  });
}

export async function fetchMyOrders(): Promise<StoreOrder[]> {
  return (await api<{ orders: StoreOrder[] }>("/portal/store/orders")).orders;
}

export async function fetchStoreOrder(id: string): Promise<StoreOrder | null> {
  try {
    return (await api<{ order: StoreOrder }>(`/portal/store/orders/${encodeURIComponent(id)}`)).order;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function payStoreOrder(id: string): Promise<PaymentSession> {
  return (await api<{ payment: PaymentSession }>(`/portal/store/orders/${encodeURIComponent(id)}/pay`, { method: "POST" })).payment;
}

export async function fetchAdminOrders(status?: OrderStatus): Promise<StoreOrder[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return (await api<{ orders: StoreOrder[] }>(`/portal/store/admin/orders${q}`)).orders;
}

/** `expectedTotal`: the total the admin saw. A 409 with `newTotal` means the
 *  price moved since — show it and let them approve again. */
export async function approveStoreOrder(id: string, expectedTotal: number | null): Promise<ApproveResult> {
  return api<ApproveResult>(`/portal/store/admin/orders/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    body: JSON.stringify(expectedTotal != null ? { expectedTotal } : {}),
  });
}

export async function rejectStoreOrder(id: string, reason: string): Promise<{ order: StoreOrder }> {
  return api<{ order: StoreOrder }>(`/portal/store/admin/orders/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function cancelStoreOrder(id: string): Promise<{ order?: StoreOrder }> {
  return api<{ order?: StoreOrder }>(`/portal/store/admin/orders/${encodeURIComponent(id)}/cancel`, { method: "POST" });
}

/** The price the server says an approval would now cost (409 { newTotal }). */
export function newTotalFrom(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const n = (err.body as { newTotal?: unknown } | null)?.newTotal;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

/** Submit's "Your cart changed — please submit again." (409): the key is spent. */
export function isCartChanged(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const m = (err.body as { error?: unknown } | null)?.error;
  return typeof m === "string" && /cart changed/i.test(m);
}

export const STORE_ORDERS_KEY = ["portal-store-orders"] as const;
export const STORE_PENDING_KEY = ["portal-store-admin-orders", "pending_approval"] as const;
export const STORE_ADMIN_TO_PAY_KEY = ["portal-store-admin-orders", "awaiting_payment", "company_card"] as const;

/** Company-card orders that are approved and waiting for the ADMIN to pay
 *  (e.g. they approved, then left Stripe without paying). */
export async function fetchAdminOrdersToPay(): Promise<StoreOrder[]> {
  return (await fetchAdminOrders("awaiting_payment")).filter((o) => o.paymentMethod === "company_card");
}
export const storeOrderKey = (id: string) => ["portal-store-order", id] as const;

// ─── Plain words ─────────────────────────────────────────────────────────

export function money(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export const pieces = (n: number) => `${n.toLocaleString("en-US")} ${n === 1 ? "piece" : "pieces"}`;

/** "Invoice to Acme (net 30)". */
export function invoiceWords(store: MyStore["store"]): string {
  const days = TERMS_DAYS[store.invoiceTerms ?? "net_30"] ?? "30";
  return `Invoice to ${store.companyName || "your company"} (net ${days})`;
}

/** One payment option, in words, for the person checking out. */
export function paymentChoiceText(option: PaymentOption, my: MyStore): { label: string; help: string } {
  switch (option) {
    case "company_card":
      return my.role === "admin"
        ? { label: "Company card", help: "You pay now with the company card." }
        : { label: "Your company card — your store admin pays", help: "Your store admin approves the order and pays for it." };
    case "invoice":
      return { label: invoiceWords(my.store), help: "Nothing to pay now. The order goes on your company's account." };
    case "employee_card":
      return { label: "Pay now with your card", help: "You pay for this order yourself, by card." };
  }
}

/**
 * Will this order wait for the admin's OK? Mirrors the API's needsApproval:
 * an admin's own orders never wait; a member's company-card order always
 * goes to an admin (they pay it); then the store's rule. "maybe" = the rule
 * is "over an amount" and members aren't told the amount.
 */
export function approvalNeeded(my: MyStore, method: PaymentOption | null, total: number | null): "yes" | "no" | "maybe" {
  if (my.role === "admin") return "no";
  if (method === "company_card") return "yes";
  const rule = my.store.approvalRule;
  if (rule === "all") return "yes";
  if (rule === "over_amount") {
    const t = my.store.approvalThreshold;
    if (t != null && total != null) return Math.round(total * 100) > Math.round(t * 100) ? "yes" : "no";
    return "maybe";
  }
  return "no";
}

export type Tone = "ok" | "warn" | "risk" | "plain";

/** An order's status, as the person who ordered it reads it. */
export function orderStatusText(o: Pick<StoreOrder, "status" | "rejectedReason" | "canPay" | "paymentMethod">): { text: string; tone: Tone } {
  switch (o.status) {
    case "pending_approval":
      return { text: "Waiting for approval", tone: "warn" };
    case "awaiting_payment":
      if (o.canPay) return { text: "Approved — pay now", tone: "warn" };
      return o.paymentMethod === "company_card"
        ? { text: "Approved — your store admin is paying", tone: "warn" }
        : { text: "Approved — waiting for payment", tone: "warn" };
    case "paying":
      return { text: "Payment processing", tone: "warn" };
    case "paid":
      return { text: "Paid", tone: "ok" };
    case "approved":
      return { text: "Approved — going into production", tone: "ok" };
    case "placed":
      return { text: "Placed — in production", tone: "ok" };
    case "rejected":
      return { text: `Declined${o.rejectedReason ? `: ${o.rejectedReason}` : ""}`, tone: "risk" };
    case "cancelled":
      return { text: "Cancelled", tone: "risk" };
    default:
      return { text: String(o.status).replace(/_/g, " "), tone: "plain" };
  }
}

export const TONE_CLASS: Record<Tone, string> = {
  ok: "bg-ok-bg text-ok",
  warn: "bg-warn-bg text-warn",
  risk: "bg-risk-bg text-risk",
  plain: "bg-canvas text-ink",
};

export function paymentMethodWords(m: PaymentOption | null): string {
  if (m === "company_card") return "Company card";
  if (m === "invoice") return "Invoice";
  if (m === "employee_card") return "Own card";
  return "";
}

/** "Classic Polo × 24, Cap × 12". */
export function itemsSummary(items: PricedItem[]): string {
  return items.map((i) => `${i.name} × ${i.quantity}`).join(", ");
}

/** May an admin cancel an order in this state? */
export function canCancel(status: OrderStatus): boolean {
  return status === "pending_approval" || status === "approved" || status === "awaiting_payment";
}

// ─── The delivery address (US only — the website cart's rules) ──────────

export const US_STATES: Array<[code: string, name: string]> = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"], ["CO", "Colorado"],
  ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"],
  ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"],
  ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"],
  ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

// The website cart's ZIP → state table (purchase.js ZIP3): first three digits.
const ZIP3: Array<[max: number, state: string]> = [
  [5, "NY"], [9, "PR"], [27, "MA"], [29, "RI"], [38, "NH"], [49, "ME"], [59, "VT"], [69, "CT"], [89, "NJ"], [99, ""],
  [149, "NY"], [196, "PA"], [199, "DE"], [205, "DC"], [219, "MD"], [246, "VA"], [268, "WV"], [289, "NC"], [299, "SC"],
  [319, "GA"], [349, "FL"], [369, "AL"], [385, "TN"], [397, "MS"], [399, "GA"], [427, "KY"], [459, "OH"], [479, "IN"],
  [499, "MI"], [528, "IA"], [549, "WI"], [567, "MN"], [569, "DC"], [577, "SD"], [588, "ND"], [599, "MT"], [629, "IL"],
  [658, "MO"], [679, "KS"], [693, "NE"], [714, "LA"], [729, "AR"], [749, "OK"], [799, "TX"], [816, "CO"], [831, "WY"],
  [838, "ID"], [847, "UT"], [865, "AZ"], [884, "NM"], [885, "TX"], [898, "NV"], [961, "CA"], [966, ""], [968, "HI"],
  [969, ""], [979, "OR"], [994, "WA"], [999, "AK"],
];

/** The state a US ZIP belongs to ("" = unknown). */
export function zipState(zip: string): string {
  const n = parseInt(zip.trim().slice(0, 3), 10);
  if (!(n >= 0)) return "";
  for (const [max, st] of ZIP3) if (n <= max) return st;
  return "";
}

export const ZIP_RE = /^\d{5}(-\d{4})?$/;

type AddressField = "name" | "street1" | "city" | "stateCode" | "postalCode";

/** What's wrong with the address, field by field (plain words). */
export function shipToProblems(s: ShipTo): Partial<Record<AddressField, string>> {
  const out: Partial<Record<AddressField, string>> = {};
  if (!s.name.trim()) out.name = "Who is it for?";
  if (!s.street1.trim()) out.street1 = "Enter the street address.";
  if (!s.city.trim()) out.city = "Enter the city.";
  if (!US_STATES.some(([c]) => c === s.stateCode)) out.stateCode = "Choose the state.";
  if (!ZIP_RE.test(s.postalCode.trim())) out.postalCode = "Enter a 5-digit US ZIP code.";
  else {
    const st = zipState(s.postalCode);
    if (st && s.stateCode && st !== s.stateCode) out.postalCode = `That ZIP code is in ${st}. Check the ZIP or the state.`;
  }
  return out;
}

/** A complete address (enough to rate shipping and submit), or null. */
export function completeShipTo(s: ShipTo | null): ShipTo | null {
  return s && Object.keys(shipToProblems(s)).length === 0 ? s : null;
}

export const EMPTY_SHIP_TO: ShipTo = {
  name: "", company: "", street1: "", street2: "", city: "", stateCode: "", postalCode: "", residential: false, phone: "",
};

export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // fall through
  }
  const part = () => Math.random().toString(36).slice(2, 10);
  return `k${Date.now().toString(36)}-${part()}-${part()}`;
}
